import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ILead, LeadSource, QuoteType } from "../../../../models/Lead";
import { ISavCustomerName } from "../../../../data/useSav";
import PriceFormat from "../../../../utils/priceFormat";
import formatDateTime from './../../../../utils/formatDateTime';
import SendQualifierButton from "../../SendQualifierButton";
import { GetQuoteType } from "../../LeadInformation/QuoteCalculationCard";
import SavCustomerPickerPill from "../SavCustomerPickerPill";
import { useAuth } from "../../../../contexts/AuthContext";
import EnginesWorldwideLookup from "../../../vehicles/EnginesWorldwideLookup";

export default function LeadsTableSubComponent({
  lead,
  isSavCustomer = false,
  savCustomers = [],
  onAllocateLead,
  allocatingLeadId = null,
}: {
  lead: ILead;
  isSavCustomer?: boolean;
  savCustomers?: ISavCustomerName[];
  onAllocateLead?: (leadId: string, targetCustomerId: string) => void;
  allocatingLeadId?: string | null;
}) {
  const { t } = useTranslation("leads");
  const { customerId } = useAuth();
  const [targetCustomerId, setTargetCustomerId] = useState<string>("");
  const allocatedCustomerName = savCustomers.find(x => x.id === lead.allocatedToCustomerId)?.name;
  const isAllocated = !!lead.allocatedToCustomerId;

  useEffect(() => {
    if (!isSavCustomer || !savCustomers.length || targetCustomerId) return;
    const defaultCustomerId = savCustomers.some(x => x.id === customerId)
      ? customerId
      : savCustomers[0].id;
    setTargetCustomerId(defaultCustomerId ?? "");
  }, [isSavCustomer, savCustomers, targetCustomerId, customerId]);


  return (
    <>
      <div className="flex justify-between bg-gray-100 pl-28 p-4">
        <div className="flex gap-x-10">
          <div className="flex flex-col">
            <span className="font-bold">{t("Price")}</span>
            <span>{PriceFormat(lead.price)}</span>
          </div>

          {lead.applyVAT && (
            <div className="flex flex-col">
              <span className="font-bold">{t("With VAT")}</span>
              <span>{PriceFormat(lead.priceWithVAT)}</span>
            </div>
          )}

          <div className="flex flex-col">
            <span className="font-bold">{t("Website")}</span>
            <span>{t(LeadSource[lead.website])}</span>
          </div>

          {(lead.website == LeadSource.SellAVehicle || lead?.website == LeadSource.Widget) && (
            <div className="flex flex-col">
              <span className="font-bold">{t("Quote Type")}</span>
              <span>{t(GetQuoteType(lead.quoteType) ?? "")}</span>
            </div>
          )}

          <div className="flex flex-col">
            <span className="font-bold">{t("Exported")}</span>
            <span className="flex justify-center">{lead.exportedOn !== null ? "✔" : "X"}</span>
          </div>

          <div className="flex flex-col">
            <span className="font-bold">{t("Created")}</span>
            <span className="flex justify-center">{formatDateTime(lead.created, DateTime.DATE_SHORT)}</span>
          </div>

          {lead.vehicle && (
            <div className="flex flex-col">
              <span className="font-bold">{t("EWW Prices")}</span>
              <EnginesWorldwideLookup vehicle={lead.vehicle} variant="inline" inlineTitle="" />
            </div>
          )}

          <div className="flex flex-col">
            <span className="font-bold">{t("VLA Engine Price")}</span>
            <span>
              {lead.vehicle?.vlaEngineOffer ?
                PriceFormat(parseFloat(lead.vehicle?.vlaEngineOffer))
                : t("N/A")
              }
            </span>
          </div>

          <div className="flex flex-col">
            <span className="font-bold">{t("VLA VFE Price")}</span>
            <span>
              {lead.vehicle?.vlaVfeOffer ?
                PriceFormat(parseFloat(lead.vehicle?.vlaVfeOffer))
                : t("N/A")
              }
            </span>
          </div>

          <div className="flex flex-col">
            <span className="font-bold">{t("Average Catalytic Price")}</span>
            <span>
              {lead.vehicle?.enginesWorldwideVfeOffer ?
                PriceFormat(parseFloat(lead.catalyticAveragePrice?.toString()!))
                : lead.vehicle?.catalyticResults && lead.vehicle.catalyticResults.length > 0 ?
                  PriceFormat(parseFloat((lead.vehicle.catalyticResults.reduce((sum, x) => sum + Number(x.value), 0) /
                    lead.vehicle.catalyticResults.length).toString()))
                  : t("N/A")
              }
            </span>
          </div>
        </div>

        <div className="flex items-center">
          <div className="flex flex-col items-end gap-2">
            <SendQualifierButton lead={lead} />

            {isSavCustomer && !isAllocated && (
              <div className="flex items-center gap-2">
                <SavCustomerPickerPill
                  value={targetCustomerId || null}
                  customers={savCustomers ?? []}
                  onChange={(id) => setTargetCustomerId(id ?? "")}
                  placeholder={t("Assign customer")}
                />

                <button
                  className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                  disabled={!targetCustomerId || allocatingLeadId === lead.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAllocateLead?.(lead.id, targetCustomerId);
                  }}
                >
                  {allocatingLeadId === lead.id ? t("Allocating...") : t("Allocate")}
                </button>
              </div>
            )}

            {isSavCustomer && isAllocated && (
              <div className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-semibold">
                {t("Allocated to {{name}}", { name: allocatedCustomerName || "-" })}
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  )
}